/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Paper, Typography, List, ListItem, ListItemAvatar,
    ListItemText, Avatar, Divider, TextField, IconButton,
    CircularProgress, Badge, Alert
} from '@mui/material';
import {
    Send as SendIcon,
    Group as GroupIcon
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { useSocket } from '../../contexts/SocketContext';

export default function ChatComponent() {
    const { user } = useAuth();
    const token = localStorage.getItem('token');
    const { socket, resetUnread } = useSocket(); // Socket do contexto

    // const [socket, setSocket] = useState(null); // REMOVIDO
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [usersError, setUsersError] = useState('');
    const messagesEndRef = useRef(null);
    const [onlineUsers, setOnlineUsers] = useState(new Set());

    // Buscar Usuários (Socket já vem pronto)
    useEffect(() => {
        if (!user || !token) return;

        // Zera notificações ao entrar na tela
        resetUnread();

        const fetchUsers = async () => {
            setLoadingUsers(true);
            setUsersError('');
            try {
                const res = await api.get('/chat/contatos');
                const usuariosArray = res.data.dados || (Array.isArray(res.data) ? res.data : []);
                setUsers(usuariosArray);
            } catch (err) {
                console.error('Erro ao buscar usuários:', err);
                setUsers([]);
                setUsersError(
                    err.response?.data?.mensagem ||
                    err.response?.data?.error ||
                    'Não foi possível carregar os contatos do chat.'
                );
            } finally {
                setLoadingUsers(false);
            }
        };
        fetchUsers();
    }, [user, token]); // Removido socket dependency daqui pois socket é estável do context

    // Ouvir eventos Socket
    useEffect(() => {
        if (!socket) return;

        const handleReceiveMessage = (msg) => setMessages((prev) => [...prev, msg]);
        const handleMessageSent = (msg) => setMessages((prev) => [...prev, msg]);
        const handleInitialUsers = (userIds) => setOnlineUsers(new Set(userIds.map(id => Number(id))));

        const handleUserStatus = ({ userId, status }) => {
            setOnlineUsers(prev => {
                const newSet = new Set(prev);
                const uid = Number(userId);
                if (status === 'online') newSet.add(uid);
                else newSet.delete(uid);
                return newSet;
            });
        };

        socket.on('receive_message', handleReceiveMessage);
        socket.on('message_sent', handleMessageSent);
        socket.on('initial_online_users', handleInitialUsers);
        socket.on('user_status', handleUserStatus);

        return () => {
            socket.off('receive_message', handleReceiveMessage);
            socket.off('message_sent', handleMessageSent);
            socket.off('initial_online_users', handleInitialUsers);
            socket.off('user_status', handleUserStatus);
        };
    }, [socket]);

    // Carregar histórico ao mudar de chat
    useEffect(() => {
        const fetchMessages = async () => {
            setLoading(true);
            try {
                const params = selectedUser ? { contactId: selectedUser.id_usuario } : { contactId: 'public' };
                const res = await api.get('/chat/mensagens', { params });
                setMessages(res.data);
            } catch (err) {
                console.error('Erro ao buscar mensagens:', err);
            } finally {
                setLoading(false);
            }
        };

        if (user) fetchMessages();
    }, [selectedUser, user]);

    // Scroll para o fim
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    const handleSendMessage = () => {
        if (!newMessage.trim() || !socket) return;

        const msgData = {
            destinatario_id: selectedUser ? selectedUser.id_usuario : null,
            mensagem: newMessage
        };

        socket.emit('send_message', msgData);
        setNewMessage('');
    };

    // Filtragem visual das mensagens recebidas
    const displayedMessages = messages.filter(m => {
        if (selectedUser) {
            // Privado: Msg entre Eu(user.id) e Ele(selectedUser.id_usuario)
            return (m.remetente_id === user.id && m.destinatario_id === selectedUser.id_usuario) ||
                (m.remetente_id === selectedUser.id_usuario && m.destinatario_id === user.id);
        } else {
            // Público: null
            return m.destinatario_id === null;
        }
    });

    return (
        <Box sx={{ display: 'flex', height: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper' }}>
            {/* Lista de Usuários */}
            <Box sx={{ width: '300px', borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6">Conversas</Typography>
                </Box>
                <List sx={{ flex: 1, overflowY: 'auto' }}>
                    <ListItem button selected={selectedUser === null} onClick={() => setSelectedUser(null)}>
                        <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'secondary.main' }}><GroupIcon /></Avatar>
                        </ListItemAvatar>
                        <ListItemText primary="Chat Geral" secondary="Toda a empresa" />
                    </ListItem>
                    <Divider />
                    {loadingUsers && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}
                    {!loadingUsers && usersError && (
                        <Alert severity="error" sx={{ m: 1 }}>{usersError}</Alert>
                    )}
                    {!loadingUsers && !usersError && users.length === 0 && (
                        <Typography color="text.secondary" variant="body2" sx={{ p: 2, textAlign: 'center' }}>
                            Nenhum outro usuário ativo na empresa.
                        </Typography>
                    )}
                    {!loadingUsers && users.map(u => (
                        <ListItem button key={u.id_usuario} selected={selectedUser?.id_usuario === u.id_usuario} onClick={() => setSelectedUser(u)}>
                            <ListItemAvatar>
                                <Badge color="success" variant="dot" invisible={!onlineUsers.has(u.id_usuario)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                                    <Avatar alt={u.st_nome} src={u.url_avatar || ''}>{!u.url_avatar && (u.st_nome?.[0] || 'U')}</Avatar>
                                </Badge>
                            </ListItemAvatar>
                            <ListItemText primary={u.st_nome} secondary={u.st_email} />
                        </ListItem>
                    ))}
                </List>
            </Box>

            {/* Área de Chat */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: 'action.hover' }}>
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        {selectedUser ? selectedUser.st_nome : "Chat Geral - Empresa"}
                    </Typography>
                </Box>

                <Box sx={{ flex: 1, p: 2, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {loading ? <CircularProgress sx={{ alignSelf: 'center', mt: 2 }} /> : (
                        displayedMessages.map((msg, i) => {
                            const isMe = msg.remetente_id === user.id;
                            return (
                                <Box key={i} sx={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                                    <Paper sx={{
                                        p: 1.5,
                                        maxWidth: '70%',
                                        bgcolor: isMe ? 'primary.main' : 'background.paper',
                                        color: isMe ? 'primary.contrastText' : 'text.primary',
                                        borderRadius: 2
                                    }}>
                                        {!isMe && <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', mb: 0.5 }}>{msg.remetente_nome}</Typography>}
                                        <Typography variant="body1">{msg.mensagem}</Typography>
                                        <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.5, opacity: 0.8, fontSize: '0.7rem' }}>
                                            {new Date(msg.data_envio).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </Typography>
                                    </Paper>
                                </Box>
                            )
                        })
                    )}
                    <div ref={messagesEndRef} />
                </Box>

                <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', gap: 1 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Digite sua mensagem..."
                        value={newMessage}
                        onChange={e => setNewMessage(e.target.value)}
                        onKeyPress={e => e.key === 'Enter' && handleSendMessage()}
                    />
                    <IconButton color="primary" onClick={handleSendMessage} disabled={!newMessage.trim()}>
                        <SendIcon />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
}
